/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { bodyToLeaves, pathToLabel, type MappingLeaf } from './ortb-body-editor.adapters';
import { suggestMapping } from '../../../utils/mapping-suggest';

/**
 * Did a **sample** bid request just land in the Raw JSON window? (#922 · 5)
 *
 * The two windows look alike and do opposite things:
 *
 * | | What it is | What a pasted sample becomes |
 * |---|---|---|
 * | **Paste & parse** | a *reader* — proposes mappings from a sample | `{ip}`, `{ua}`, `{ifa}` … |
 * | **Raw JSON** | *the template itself* | one viewer's IP, UA and device id, **frozen** |
 *
 * Pasting into the wrong one is completely silent and produces a source that sends one
 * publisher's captured constants to every viewer, forever. That is the same harm #952
 * fixed on the VAST paste path — and worse here, because nothing at all is filtered: the
 * whole document is taken literally.
 *
 * **Offer the choice; never guess.** The operator may legitimately want a literal value
 * (a `tagid`, an `at`, a publisher id), and silently macro-mapping the document would be
 * the mirror-image defect. So this only ever *asks*.
 *
 * ## What counts as evidence
 *
 * The signal is not "does it look like a BidRequest" — the template legitimately *is* a
 * BidRequest and always will be, so shape alone fires on every correct edit. The signal
 * is **captured per-viewer values**: leaves the shared `suggestMapping` classifier would
 * map to a macro, currently holding a literal.
 *
 * That reuses the classifier #952 already validated rather than adding a second opinion
 * about what a per-viewer value is, and it has the property that matters: it cannot fire
 * on a template that is already correctly mapped, because a mapped leaf holds a macro.
 *
 * Returns `null` when there is nothing to say. A single stray literal is not evidence of
 * a paste — an operator pinning one value is an ordinary edit — so it takes
 * {@link MIN_PINNED_VALUES}.
 */
export interface PastedSampleDiagnosis {
  /** Dotted labels of the leaves holding a per-viewer literal, worst-first order. */
  pinned: string[];
  /** What each would become if mapped, keyed by label — shown so the offer is concrete. */
  proposed: Record<string, string>;
}

/**
 * Two, not one. An operator deliberately pinning a single value — a test IP while
 * debugging, a fixed device id to reproduce a bid — is a normal edit, and prompting on it
 * would train them to dismiss the prompt. A whole captured request carries several.
 */
export const MIN_PINNED_VALUES = 2;

export function diagnosePastedSample(body: Record<string, unknown>): PastedSampleDiagnosis | null {
  let leaves: MappingLeaf[];
  try {
    leaves = bodyToLeaves(body);
  } catch {
    return null;
  }

  const pinned: string[] = [];
  const proposed: Record<string, string> = {};

  for (const leaf of leaves) {
    // Only a literal can be pinned. A leaf already carrying a macro or an expression is
    // mapped — which is why a correct template never trips this.
    if (leaf.kind !== 'static') continue;
    const value = leaf.staticValue;
    if (value == null || value === '') continue;
    if (typeof value === 'object') continue;

    const key = String(leaf.path[leaf.path.length - 1]);
    const suggestion = suggestMapping(key, value, 'body');
    if (suggestion.kind !== 'macro' || !suggestion.macroToken) continue;

    const label = pathToLabel(leaf.path);
    pinned.push(label);
    proposed[label] = suggestion.macroToken;
  }

  if (pinned.length < MIN_PINNED_VALUES) return null;
  return { pinned, proposed };
}
